import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import FileUploadIcon from '@material-design-icons/svg/filled/file_upload.svg?react';
import type React from 'react';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { useFileUploadPreview } from '../../hooks/useFileUploadPreview';
import { typedError } from '../../lib/typedError';
import type { FileInputError } from '../../types/fileUpload';
import { RemoveButton } from '../common/RemoveButton';

interface BaseUploadInputProps {
  name: string;
  label?: string;
  description?: string;
  dimensions?: string;
  value?: File | null;
  onChange: (file: File | null) => void;
  onError?: (error: FileInputError | null) => void;
  className?: string;
  placeholderClassName?: string;
  buttonType?: 'add' | 'upload';
  buttonClassName?: string;
  accept: string;
  acceptedTypes: string[];
  maxSize?: number;
  renderPreview: (url: string, file: File) => React.ReactNode;
  validateFile?: (file: File) => { valid: boolean; error?: FileInputError };
}

async function cloneFileToMemory(file: File): Promise<File> {
  const buffer = await file.arrayBuffer();
  return new File([buffer], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

export function BaseUploadInput({
  name,
  label,
  description,
  dimensions,
  value = null,
  onChange,
  onError,
  className,
  placeholderClassName,
  buttonType = 'add',
  buttonClassName,
  accept,
  acceptedTypes,
  maxSize,
  renderPreview,
  validateFile,
}: BaseUploadInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    previewUrl,
    isDragOver,
    setIsDragOver,
    handleDragOver,
    handleDragLeave,
  } = useFileUploadPreview(value);

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange(null);
    onError?.(null);
  };

  const handlePlaceholderClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileValidation = (file: File): boolean => {
    if (!acceptedTypes.includes(file.type)) {
      onError?.(typedError('InvalidInput', 'File type not supported'));
      return false;
    }

    if (maxSize && file.size > maxSize) {
      onError?.(typedError('FileSizeExceeded', 'File size is too large'));
      return false;
    }

    if (validateFile) {
      const result = validateFile(file);
      if (!result.valid && result.error) {
        onError?.(result.error);
        return false;
      }
    }

    return true;
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!handleFileValidation(file)) {
        return;
      }

      try {
        const clonedFile = await cloneFileToMemory(file);
        onChange(clonedFile);
        onError?.(null);
      } catch {
        onChange(null);
        onError?.(
          typedError(
            'InvalidInput',
            'Selected file could not be read. Please re-select it and try again.',
          ),
        );
      }
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!handleFileValidation(file)) {
        e.target.value = '';
        return;
      }

      try {
        const clonedFile = await cloneFileToMemory(file);
        onChange(clonedFile);
        onError?.(null);
      } catch {
        e.target.value = '';
        onChange(null);
        onError?.(
          typedError(
            'InvalidInput',
            'Selected file could not be read. Please re-select it and try again.',
          ),
        );
      }
    } else {
      onChange(null);
      onError?.(null);
    }
  };

  const Icon = buttonType === 'upload' ? FileUploadIcon : AddIcon;

  return (
    <div className={twMerge('flex-1 flex flex-col gap-1', className)}>
      {(label || dimensions) && (
        <div className="self-stretch">
          {label && (
            <span className="text-white text-sm font-bold leading-tight">
              {label}:{' '}
            </span>
          )}
          {dimensions && (
            <span className="text-white text-sm font-normal leading-tight">
              {dimensions}
            </span>
          )}
        </div>
      )}
      {description && (
        <div className="self-stretch text-neutral-400 text-sm font-normal leading-tight">
          {description}
        </div>
      )}

      <div className={twMerge('relative', placeholderClassName)}>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          name={name}
          aria-label={`Upload ${label || name}`}
          className="hidden"
          onChange={handleInputChange}
        />
        {previewUrl && value ? (
          <div className="relative w-full h-full border border-neutral-700 rounded-md overflow-hidden">
            {renderPreview(previewUrl, value)}
            <RemoveButton
              onClick={handleRemove}
              ariaLabel={`Remove ${label || name}`}
            />
          </div>
        ) : (
          <div
            onClick={handlePlaceholderClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={twMerge(
              'w-full h-full flex items-center justify-center cursor-pointer bg-neutral-900 hover:bg-neutral-800',
              'border border-neutral-700 rounded-md transition-all duration-200',
              isDragOver && 'border-teal-500 bg-neutral-800 scale-105',
              placeholderClassName,
            )}
            role="button"
            aria-label={`Upload ${label || name}`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handlePlaceholderClick();
            }}
          >
            <div
              className={twMerge(
                'p-2 inline-flex justify-center items-center gap-1 pointer-events-none',
                buttonClassName,
              )}
            >
              <Icon
                className={twMerge(
                  'w-5 h-5 text-white fill-current',
                  isDragOver && 'text-teal-500',
                )}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
